import { DiscountGroupDto, IFormOverviewModel, ItemListingType, VolunteerShiftDto } from "./items.interface";

export interface ICampaignOrganiserContact {
    name?: string;
    email?: string;
    phone?: string;
}

export interface ICampaignBase {
    id: number;
    campaignDisplayName: string;
    communityId: number;
    startDate?: Date | string;
    endDate?: Date | string;
    blobUrls: string;
    documentBlobUrls: string;
    about: string;
    detail: string;
    participate?: string;
    status: number;
    type: number;
    registrationSubType?: number | null; // RegistrationCampaignSubTypeEnum value, only set for Registration campaigns
    isPublic: boolean;
    accessType: number;
    itemDisplayView: number;
    templateId?: number;
    disbursementAccountId?: number | null;
    isGenerateDonationReceipt?: boolean;
    isMapViewEnabled: boolean | null;
    isShowFundraisingBar?: boolean | null;
    totalGoalAmount?: number | null;
    totalCollectedAmount?: number | null;
    isItemsSortAscending?: boolean;
    isGenerateDonationItem?: boolean | null;
    updatedOn?: Date;
    isShowTourStop: boolean;
    sortType?: number | null; 
    sortTypeDonated?: number | null;
    linkedItemStops?:Record<number, number>;
    linkedItemPlaques?:Record<number, number>;
    campaignCustomMessage?:string;
    eventAddress?: string;
    latitude?: number;
    longitude?: number;
    organiserContactsJson?: ICampaignOrganiserContact[];
    isDisbursementLocked: boolean;
    requireApplicantApproval: boolean;
    isMaxTicketCapacityEnabled?: boolean;
    maxTicketCapacity?: number | null;
}

export interface ICampaign extends ICampaignBase {
    id: number;
    campaignDisplayId: string;
    isGenerateDonationItem: boolean;
    isGenerateDonationReceipt: boolean;
}

export interface ICampaignItemGrid extends ICampaignItem {
    galleryCount?: number;
    documentCount?: number;
    isDonorActivityAssociated?: boolean;
    hasDiscountGroups?: boolean;
    discountGroupMinPrice?: number | null;
    discountGroupMaxPrice?: number | null;
}

export interface ICampaignDto {
    campaign: ICampaignBase;
    itemCount: number;
}

export interface ICampaignItemDto {
    id: number;
    campaignDisplayName: string;
    campaignDisplayId: string;
    campaignType: number;
    communityId: number;
    items: ICampaignItem[];
    isDuringCreation: boolean;
}

export interface ICampaignItem {
    id?: string;
    isNew?: boolean;
    itemDetails: ItemDetails;
    location?: LocationDetails;
    priceModel: PriceModel;
    donation?: Donation;
    itemListingType?: ItemListingType
    itemEventModel?: ItemDates;
    formOverviewModel?: IFormOverviewModel;
    shifts?: VolunteerShiftDto[];
    discountGroups?: DiscountGroupDto[];
    isCloseItem?: boolean;
}
export interface ItemDates {
    hasEventTimeEntered?: boolean;
    itemDateEventType?: number;
    itemEventStartTime?: string | null;
    itemEventEndTime?: string | null;
    itemEventTimeZone?: number | null;
    hasShifts?: boolean;
}
export interface ItemDetails {
    name: string;
    typeId: number;
    statusId: number;
    description: string;
    isPosted: string;
    gallaryBlob: string;
    documentsBlob?: string;
    itemId?: string;
    accessType?:number;
    itemCustomMessage?:string;
}

export interface LocationDetails {
    locationName: string;
    address?: string;
    longitude?: number;
    latitude?: number;
}

export interface PriceModel {
    price?: number;
    customMinPrice?: number;
    customMaxPrice?: number;
    quantity: number;
    remainingQuantity?: number;
    participationMethod?: number;
    customItemFormLink?: string;
    isAcceptPaymentOnline: boolean;
    formType: number;
    formMasterId?: number | null;
    formTemplateMasterId?: number | null;
    sortOrder?: number | null;
    isUnlimitedParticipation?: boolean;
    isFreeTicket?: boolean | null;
    hasDiscountGroups?: boolean;
    discountGroups?: DiscountGroupDto[];
}

export interface Donation {
    donatedBy: string;
    dedicatedTo: string;
    qrPlaque: string;
    qrPlaqueName?: string;
}

export interface CampaignTemplateBase {
    id: number;
    displayName: string;
    backgroundImageBlob: string;
    typeId: number;
    typeName: string;
    registrationSubType?: number | null; // RegistrationCampaignSubTypeEnum value, only set for Registration templates
    galleryImageBlob:string;
}

export interface CampaignTemplateModel extends CampaignTemplateBase {
    about: string;
    details: string;
    photoGalleryBlob: string;
    documentsBlob: string;
    startDate?: Date;
    endDate?: Date;
    statusId: number;
    statusName: string;
    campaignTemplateItems: CampaignTemplateItemModel[]
    isShowTourStop: boolean;
    sortType?: number | null;
    sortTypeDonated?: number | null;
}

export interface CampaignTemplateItemModel {
    id: number;
    displayName: string;
    typeId: number;
    typeName: string;
    statusId: number;
    statusName: string;
    activatePaymentFeature: number;
    itemDescription: string;
    photosBlobUrl: string;
    quantity: number;
    price: number;
    itemListingType: ItemListingType;
    customMinPrice?: number;
    customMaxPrice?: number;
    formTypeId?: number;
    masterFormTemplateId?: number;
    sortOrder?: number | null;
    isUnlimitedParticipation?: boolean;
    isFreeTicket?: boolean | null;
}


export interface ICampaignCardItem {
    id: number;
    imageSrc: string;
    heading: string;
    subHeading: string;
    totalItems: number;
    donatedItems: number;
    availableItems: number;
    isCompleted?: boolean;
    isPublic?: boolean;
    bulkItemsInProgress?: boolean;
    typeId: number;
    stringStartDate?: string;
    stringEndDate?: string;
    about?:string;
    accessType: number;
    isFeatured?: boolean;
    registrationSubType?: number | null;
}

export interface ILinkedCampaignListDto {
    id: number;
    campaignDisplayName: string;
    communityId: number | null;
    accessType: number;
    type: number | null;
    blobUrls: string;
    createdOn: string;
    updatedOn: string | null;
}

export interface ICampaignTypeMessage {
    available: string;
    progress: string;
    donated: string;
    completed: string;
}

export interface ICampaignTypeSectionMessage {
    available: string;
    availablesubsection1: string;
    availablesubsection2: string;
    donated: string;
    donatedsubsection1: string;
    donatedsubsection2: string;
}

export interface ICampaignDetailModel extends ICampaignBase {

    campaignDisplayId: string;
    isGenerateDonationItem: boolean;
    isGenerateDonationReceipt: boolean;
    isCompleted: boolean;
    communityName: string;
    totalItems: number;
    donatedItems: number;
    availableItems: number;
    typeName: string;
    campaignId: string
    bulkItemsInProgress: boolean;
    stringStartDate: string;
    stringEndDate: string;
    campaignTerm: string;
    isShowFundraisingBar?: boolean;
    totalGoalAmount?: number;
    standardFormSchema?: string;
    standardFormTitle?: string;
    standardFormDescription?: string;
    isAICampaign?:boolean;
}

export interface ILinkedInitiativeModel{
  id: number;
  initiativeDisplayName: string;
  communityId: number;
  status: number;
  coverBlobUrl: string;
  createdOn: string;
  updatedOn: string;

}
export interface ICamapignListViewModel {
    id: number;
    campaignDisplayName: string;
    campaignDisplayId: string;
    isCampaignCompleted: boolean;
    isPublic?: boolean;
    totalItems: number;
    totalDonatedItems: number;
    totalAvailableItems: number;
    blobUrl: string;
    camapignType: number;
    camapignTypeName: string;
    bulkItemsInProgress: boolean
    stringStartDate: string;
    stringEndDate: string;
    isMapViewEnabled?: boolean | null;
    about?:string;
    accessType: number;
    isFeatured?: boolean;
    registrationSubType?: number | null;
}

export interface ISelectedPlaque {
    itemId: string;
    plaqueId: number;
    plaqueName: string;
}

export interface IBuildItems {
    campaignItem: ICampaignItem;
    selectedPlaque: ISelectedPlaque;
}

export interface IVolunteerShift {
    id?: number;
    shiftTitle: string;
    eventDate: string;
    startTime: string;
    endTime: string;
    quantity: number;
    donationCompletedCount?: number;
    startTimeAPM?: string;
    endTimeAPM?: string;
}

export interface IGroupedCampaignItem {
    id: number;
    name: string;
    isFeatured: boolean;
    sortOrder: number;
}


